import { apiGet, apiGetPaginated, apiPatch, apiPost, PaginatedResult } from '../client'

export type CaseType = 'H1B' | 'GREEN_CARD' | 'OPT_EXTENSION' | 'STEM_OPT' | 'TN_VISA' | 'L1_VISA' | 'O1_VISA' | 'EB1' | 'EB2' | 'EB3' | 'OTHER'
export type CaseStatus = 'OPEN' | 'IN_PROGRESS' | 'PENDING_DOCUMENTS' | 'SUBMITTED' | 'APPROVED' | 'DENIED' | 'CLOSED' | 'ON_HOLD'
export type TaskStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'BLOCKED'

export interface CaseTask {
  assignedTo?: string
  completedAt?: string | null
  description?: string
  dueDate?: string
  id: string
  status: TaskStatus
  title: string
}

export interface CaseDocument {
  createdAt: string
  id: string
  name: string
  type: string
  url: string
}

export interface CaseTimelineEvent {
  [key: string]: unknown
  createdAt: string
  description: string
  eventType: string
  id: string
  metadata?: Record<string, unknown> | null
}

export interface BackendCase {
  [key: string]: unknown
  _count?: { documents?: number; tasks?: number }
  clientId?: string
  createdAt?: string
  deadline?: string
  description?: string
  documents?: CaseDocument[]
  id: string
  lawyerId?: string
  notes?: string
  priority?: string
  status: CaseStatus
  tasks?: CaseTask[]
  timeline?: CaseTimelineEvent[]
  title: string
  type: CaseType
  updatedAt?: string
  uscisNumber?: string
}

export interface CreateCasePayload {
  appointmentId?: string
  deadline?: string
  description?: string
  notes?: string
  priority?: string
  title: string
  type: CaseType
  uscisNumber?: string
}

export interface UpdateCasePayload {
  closedReason?: string
  deadline?: string
  description?: string
  notes?: string
  priority?: string
  status?: CaseStatus
  title?: string
  uscisNumber?: string
}

export interface CaseStats {
  approved: number
  denied: number
  inProgress: number
  open: number
  submitted: number
  total: number
}

export interface CreateTaskPayload {
  assignedTo?: string
  description?: string
  dueDate?: string
  title: string
}

export interface UpdateTaskPayload {
  description?: string
  dueDate?: string
  status?: TaskStatus
  title?: string
}

export const listCases = (params?: { limit?: number; page?: number; status?: CaseStatus; type?: CaseType }): Promise<PaginatedResult<BackendCase[]>> =>
  apiGetPaginated<BackendCase[]>('/cases', { params })

export const getCaseStats = () => apiGet<CaseStats>('/cases/stats')

export const createCase = (payload: CreateCasePayload) => apiPost<BackendCase>('/cases', payload)

export const getCase = (id: string) => apiGet<BackendCase>(`/cases/${id}`)

export const updateCase = (id: string, payload: UpdateCasePayload) => apiPatch<BackendCase>(`/cases/${id}`, payload)

export const getCaseTimeline = (id: string) => apiGet<CaseTimelineEvent[]>(`/cases/${id}/timeline`)

export const createCaseTask = (caseId: string, payload: CreateTaskPayload) => apiPost<CaseTask>(`/cases/${caseId}/tasks`, payload)

export const updateCaseTask = (taskId: string, payload: UpdateTaskPayload) => apiPatch<CaseTask>(`/cases/tasks/${taskId}`, payload)
